import React, { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/apiClient';
import PasswordInput from '../components/PasswordInput';
import { getPasswordPolicyError, PASSWORD_POLICY_MESSAGE } from '../utils/passwordPolicy';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Icon Components
const UserIcon = () => (
  <svg className="account-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const CreditCardIcon = () => (
  <svg className="account-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const ShieldIcon = () => (
  <svg className="account-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const AlertCircleIcon = () => (
  <svg className="account-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

function Account({ setIsAuthenticated = () => {} }) {
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [securityError, setSecurityError] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordPolicyError, setPasswordPolicyError] = useState('');
  const [passwordChanging, setPasswordChanging] = useState(false);
  const navigate = useNavigate();

  const fetchUserInfo = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(`${API_BASE_URL}/me`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user information');
      }

      const data = await response.json();
      setUserInfo(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo]);

  const authenticatedRequest = async (path, body, method = 'POST') => {
    const response = await apiFetch(`${API_BASE_URL}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Security update failed');
    return data;
  };

  const changePassword = async (event) => {
    event.preventDefault();
    setSecurityError('');
    const policyError = getPasswordPolicyError(newPassword);
    if (policyError) {
      setPasswordPolicyError(policyError);
      setSecurityError(policyError);
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setSecurityError('New passwords do not match.');
      return;
    }

    setPasswordChanging(true);
    try {
      await authenticatedRequest('/account/password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setPasswordPolicyError('');
      setIsAuthenticated(false);
      navigate('/login', { replace: true });
    } catch (requestError) {
      setSecurityError(requestError.message);
    } finally {
      setPasswordChanging(false);
    }
  };

  const getPlanDisplay = (planType) => {
    if (planType === 'unlimited') return 'Premium';
    if (planType === 'pro') return 'Pro';
    return 'Free';
  };

  const getStatusDisplay = (status) => {
    if (!status) return null;
    if (status === 'active') return 'Active';
    if (status === 'trialing') return 'Trial';
    if (status === 'canceled') return 'Canceled';
    if (status === 'past_due') return 'Past Due';
    if (status === 'unpaid') return 'Unpaid';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusClass = (status) => {
    if (!status) return '';
    if (status === 'active') return 'active';
    if (status === 'trialing') return 'trialing';
    if (status === 'canceled') return 'canceled';
    if (status === 'past_due' || status === 'unpaid') return 'past_due';
    return '';
  };

  const getBillingCycleDisplay = (billingCycle) => {
    if (billingCycle === 'monthly') return 'Monthly';
    if (billingCycle === 'yearly') return 'Yearly';
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateString;
    }
  };

  const hasActiveSubscription = () => {
    if (!userInfo) return false;
    if (userInfo.plan_type === 'free') return false;
    if (userInfo.stripe_subscription_status === 'canceled') return false;
    if (!userInfo.stripe_subscription_id) return false;
    return true;
  };

  if (loading) {
    return (
      <div className="account-page authenticated-page">
        <div className="account-container authenticated-page-content">
          <div className="account-loading">
            <p>Loading your account information...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="account-page authenticated-page">
      <div className="account-container authenticated-page-content">
        <div className="account-header">
          <h1 className="account-title">Account</h1>
          <p className="account-subtitle">
            Manage your profile, subscription, and security settings
          </p>
        </div>

        {error && (
          <div className="account-error">
            <AlertCircleIcon />
            <span>{error}</span>
          </div>
        )}

        {userInfo && (
          <>
            {/* Account Overview Card */}
            <div className="account-section">
              <div className="account-section-header">
                <div className="account-section-icon-wrapper">
                  <UserIcon />
                </div>
                <h2 className="account-section-title">Account Overview</h2>
              </div>
              <div className="account-section-body">
                <div className="account-detail-row">
                  <span className="account-detail-label">Email Address</span>
                  <span className="account-detail-value">{userInfo.email}</span>
                </div>
                <div className="account-detail-row">
                  <span className="account-detail-label">Current Plan</span>
                  <span className="account-detail-value">{getPlanDisplay(userInfo.plan_type)}</span>
                </div>
                {hasActiveSubscription() && userInfo.stripe_subscription_status && (
                  <div className="account-detail-row">
                    <span className="account-detail-label">Subscription Status</span>
                    <div className={`account-status-badge ${getStatusClass(userInfo.stripe_subscription_status)}`}>
                      {getStatusDisplay(userInfo.stripe_subscription_status)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Subscription & Billing Card */}
            <div className="account-section">
              <div className="account-section-header">
                <div className="account-section-icon-wrapper account-section-icon-secondary">
                  <CreditCardIcon />
                </div>
                <h2 className="account-section-title">Subscription & Billing</h2>
              </div>
              <div className="account-section-body">
                <p className="account-section-description">
                  View and manage your subscription plan, payment methods, billing history, and invoices.
                </p>
                
                {hasActiveSubscription() && (
                  <div className="account-billing-summary">
                    <div className="account-detail-row">
                      <span className="account-detail-label">Plan</span>
                      <span className="account-detail-value">{getPlanDisplay(userInfo.plan_type)}</span>
                    </div>
                    {userInfo.billing_cycle && (
                      <div className="account-detail-row">
                        <span className="account-detail-label">Billing Cycle</span>
                        <span className="account-detail-value">{getBillingCycleDisplay(userInfo.billing_cycle)}</span>
                      </div>
                    )}
                    {userInfo.stripe_current_period_end && !userInfo.stripe_cancel_at_period_end && (
                      <div className="account-detail-row">
                        <span className="account-detail-label">Next Billing Date</span>
                        <span className="account-detail-value">{formatDate(userInfo.stripe_current_period_end)}</span>
                      </div>
                    )}
                    {userInfo.stripe_cancel_at_period_end && userInfo.stripe_current_period_end && (
                      <div className="account-detail-row">
                        <span className="account-detail-label">Access Ends</span>
                        <span className="account-detail-value">{formatDate(userInfo.stripe_current_period_end)}</span>
                      </div>
                    )}
                  </div>
                )}

                {!hasActiveSubscription() && (
                  <div className="account-no-subscription">
                    <p className="account-no-subscription-text">
                      {userInfo.access_source === 'admin_complimentary'
                        ? `Your ${getPlanDisplay(userInfo.plan_type)} access is complimentary and has no Stripe charge.`
                        : 'You are currently on the Free plan. Upgrade to unlock more features.'}
                    </p>
                  </div>
                )}

                <div className="account-section-actions">
                  <Link to="/billing" className="account-btn-primary">
                    Manage Billing & Subscription
                  </Link>
                  {!hasActiveSubscription() && (
                    <Link to="/pricing" className="account-btn-secondary">
                      View Plans & Pricing
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* Security Card */}
            <div className="account-section">
              <div className="account-section-header">
                <div className="account-section-icon-wrapper">
                  <ShieldIcon />
                </div>
                <h2 className="account-section-title">Security</h2>
              </div>
              <div className="account-section-body">
                <p className="account-section-description">
                  Protect your account with a six-digit sign-in code sent to your verified email address.
                </p>
                {securityError && <div className="account-error" role="alert">{securityError}</div>}
                <section className="account-security-panel" aria-labelledby="email-sign-in-verification-title">
                  <h3 id="email-sign-in-verification-title">Email sign-in verification</h3>
                  <p>After your password is accepted, Black Peak sends a one-time code to your verified email address. Codes expire after 15 minutes and can be used only once.</p>
                  <p>If you did not try to sign in, you can ignore the code. Contact support if you cannot access your verified email address.</p>
                </section>
                <section className="account-security-panel" aria-labelledby="change-password-title">
                  <h3 id="change-password-title">Change Password</h3>
                  <p>Choose a password with at least 8 characters, uppercase and lowercase letters, and a number or symbol. You will need to sign in again after it changes.</p>
                  <form className="account-password-form" onSubmit={changePassword}>
                    <label className="login-label" htmlFor="change-current-password">Current password</label>
                    <PasswordInput
                      id="change-current-password"
                      label="current password"
                      aria-label="Current password for password change"
                      autoComplete="current-password"
                      required
                      value={currentPassword}
                      onChange={(event) => setCurrentPassword(event.target.value)}
                    />
                    <label className="login-label" htmlFor="change-new-password">New password</label>
                    <PasswordInput
                      id="change-new-password"
                      label="new password"
                      autoComplete="new-password"
                      minLength={8}
                      required
                      value={newPassword}
                      onChange={(event) => {
                        const nextPassword = event.target.value;
                        setNewPassword(nextPassword);
                        const nextPolicyError = getPasswordPolicyError(nextPassword);
                        setPasswordPolicyError(nextPolicyError);
                        if (!nextPolicyError) setSecurityError('');
                      }}
                    />
                    <p className="password-policy-hint">{PASSWORD_POLICY_MESSAGE}</p>
                    {passwordPolicyError && <p className="password-policy-error" role="alert">{passwordPolicyError}</p>}
                    <label className="login-label" htmlFor="change-confirm-password">Confirm new password</label>
                    <PasswordInput
                      id="change-confirm-password"
                      label="confirm new password"
                      autoComplete="new-password"
                      minLength={8}
                      required
                      value={confirmNewPassword}
                      onChange={(event) => setConfirmNewPassword(event.target.value)}
                    />
                    <div className="account-section-actions">
                      <button className="account-btn-primary" type="submit" disabled={passwordChanging}>
                        {passwordChanging ? 'Changing Password…' : 'Change Password'}
                      </button>
                    </div>
                  </form>
                </section>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Account;
